import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import SectionHeader from '../SectionHeader';
import ScrollReveal from '../animated/ScrollReveal';
import { colors, gradients } from '../../theme/designTokens';

const ERM_PILLARS = [
  {
    icon: <AnalyticsIcon />,
    title: 'Risk Assessment & Strategy',
    description: 'Objective analysis of exposures, benchmarks and strategic risk targets across your value chain.',
  },
  {
    icon: <AccountBalanceWalletIcon />,
    title: 'Financial Risk & Protection',
    description: 'How well risk is financed — coverage adequacy, efficiency and liability exposure management.',
  },
  {
    icon: <SettingsIcon />,
    title: 'Operations & Governance',
    description: 'Risk embedded in processes, controls, accountability and day-to-day operational decision-making.',
  },
];

const SAMPLE_SCORE = {
  composite: 86,
  ermEffectiveness: 88,
  insuranceEfficiency: 84,
  quadrant: 'Leaders',
  quadrantDescription:
    'Strong ERM and efficient protection architecture — management and risk transfer aligned and well matched.',
};

const ErmScorecard = () => (
  <Box
    component="section"
    sx={{
      px: { xs: 2, sm: 3, md: 4 },
      py: { xs: 8, md: 12 },
      background: colors.offWhite,
    }}
  >
    <ScrollReveal>
      <SectionHeader
        eyebrow="Your ERM Scorecard"
        title="Where your organization stands"
        highlightWords={['stands']}
        subtitle="A clear snapshot of enterprise risk management strength and protection efficiency—built from your financials and governance practices."
      />
    </ScrollReveal>

    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: { xs: 4, lg: 5 },
        alignItems: 'start',
      }}
    >
      <ScrollReveal>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {ERM_PILLARS.map(({ icon, title, description }) => (
            <Box
              key={title}
              sx={{
                display: 'flex',
                gap: 2,
                p: 2.5,
                background: colors.white,
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  background: colors.navy,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: colors.silver,
                  '& svg': { fontSize: 22 },
                }}
              >
                {icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, color: colors.navy, mb: 0.5, fontSize: '0.95rem' }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: colors.slate, lineHeight: 1.6 }}>
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </ScrollReveal>

      <ScrollReveal delay={0.1}>
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            background: gradients.navy,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: colors.silver,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              mb: 1,
            }}
          >
            Composite ERM Efficiency Index
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: '3.5rem',
                fontWeight: 500,
                color: colors.white,
                lineHeight: 1,
              }}
            >
              {SAMPLE_SCORE.composite}
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.55)' }}>/ 100</Typography>
          </Box>

          {[
            { label: 'ERM Effectiveness', value: SAMPLE_SCORE.ermEffectiveness },
            { label: 'Protection Efficiency', value: SAMPLE_SCORE.insuranceEfficiency },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.85)' }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.silver }}>{value}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                  height: 4,
                  borderRadius: 0,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 0,
                    background: colors.silver,
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: 2.5,
            background: colors.white,
            border: `1px solid ${colors.borderLight}`,
            borderLeft: `2px solid ${colors.navy}`,
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: colors.navy, mb: 0.5 }}>
            Quadrant: {SAMPLE_SCORE.quadrant}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: colors.slate, lineHeight: 1.65 }}>
            {SAMPLE_SCORE.quadrantDescription}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.8rem', color: colors.slate, fontStyle: 'italic', lineHeight: 1.6 }}>
          Sample scorecard for illustration. Your index is calculated from your organization&apos;s actual risk data during a baseline assessment.
        </Typography>
      </ScrollReveal>
    </Box>
  </Box>
);

export default ErmScorecard;
